import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, DrawerProps } from '@avangard/ui/core';
import { black, green, red, white, gray } from '@avangard/ui/colors';

import { CommonHelper } from '@helpers';
import { Loader } from '@modules/layout/moleculas';
import { useTestResults } from '@modules/testing/hooks';
import { StudentSubjectDetailedResultsDrawer } from './student-subject-detailed-results-drawer';
import { makeStyles } from '@material-ui/core';

export type StudentSubjectResultsDrawerProps = Pick<DrawerProps, 'open' | 'onClose'> & {
    userId?: string;
    courseId?: string;
    subject?: {
        id?: string;
        name?: string;
        score?: number;
    };
};

const Score = styled.div`
    flex: 0 0 auto;
    margin-bottom: 24px;

    color: ${black[70]};
    font-weight: 600;
    font-size: 2.4rem;
    line-height: 1.4;
`;

const Questions = styled.div`
    margin: -6px;
`;
const QuestionRow = styled.div`
    display: flex;
    padding: 6px;
    border-radius: 5px;
    transition: ease-in-out 0.2s;
    cursor: pointer;

    & + & {
        margin-top: 12px;
    }

    &:hover {
        background: ${gray[20]};
        opacity: 0.9;
    }
`;

const QuestionNumber = styled.div<{ isCorrect: boolean; userAnswered: boolean }>`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: 32px;
    width: 32px;
    margin-right: 12px;
    color: ${white[100]};
    font-weight: 600;
    font-size: 1.4rem;
    border-radius: 2px;
    border: 1px solid transparent;
    background: ${p => (p.isCorrect ? green[100] : p.userAnswered ? red[100] : gray[80])};
`;

const AnswersList = styled.ul`
    display: flex;
    counter-reset: list;
`;

const AnswerItem = styled.li<{ isCorrect: boolean; isSelected: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    height: 32px;
    width: 32px;
    color: ${p => (p.isCorrect ? green[100] : !p.isCorrect && p.isSelected ? red[100] : 'unset')};
    font-size: 1.4rem;
    font-weight: 600;
    border-width: 1px;
    border-style: solid;
    border-radius: 2px;
    border-color: ${p => (p.isSelected ? gray[40] : 'transparent')};

    counter-increment: list;

    &:before {
        content: counter(list, decimal);
    }

    & + & {
        margin-left: 8px;
    }
`;

const useDrawerStyles = makeStyles({
    title: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
});

const DrawerLoader = styled(Loader)`
    height: 100vh;

    @supports (height: 100dvh) {
        height: 100dvh;
    }
`;

const StudentSubjectResultsDrawer = (
    props: StudentSubjectResultsDrawerProps,
): React.ReactElement => {
    const { open, courseId, userId, subject, onClose } = props;

    const drawerClasses = useDrawerStyles();

    const { t } = useTranslation(['common', 'testing']);

    const [currentQuestionNumber, setCurrentQuestionNumber] = React.useState(0);

    const { testResults, loading: testResultsLoading } = useTestResults({
        fetchPolicy: 'network-only',
        skip: !courseId || !userId,
        variables: {
            courseId,
            subjectId: subject?.id,
            userId,
        },
    });

    const displayScore = CommonHelper.createCountFormatter(subject?.score ?? 0, [
        t('common:blocks.scores.one'),
        t('common:blocks.scores.some'),
        t('common:blocks.scores.multiple'),
    ]);

    const handleCloseDetailedResults = React.useCallback(() => {
        setCurrentQuestionNumber(0);
    }, []);

    const handleChangeCurrentQuestionNumber = React.useCallback((num: number) => {
        setCurrentQuestionNumber(num);
    }, []);

    const handleClickQuestionNumber = React.useCallback(
        (num: number) => () => {
            setCurrentQuestionNumber(num);
        },
        [],
    );

    const content = React.useMemo(() => {
        if (testResultsLoading) {
            return <DrawerLoader />;
        }

        return (
            <>
                <Score>{displayScore}</Score>

                <Questions>
                    {testResults.questions.map((question, index) => {
                        return (
                            <QuestionRow
                                key={question.id}
                                onClick={handleClickQuestionNumber(index + 1)}
                            >
                                <QuestionNumber
                                    isCorrect={question.isCorrect}
                                    userAnswered={question.userAnswered}
                                >
                                    {index + 1}
                                </QuestionNumber>

                                <AnswersList>
                                    {question.answers.map(answer => {
                                        return (
                                            <AnswerItem
                                                key={answer.id}
                                                isCorrect={answer.isCorrect}
                                                isSelected={answer.userAnswered}
                                            />
                                        );
                                    })}
                                </AnswersList>
                            </QuestionRow>
                        );
                    })}
                </Questions>
            </>
        );
    }, [testResults, testResultsLoading]);

    return (
        <>
            <Drawer
                externalClasses={drawerClasses}
                open={open}
                title={subject?.name ?? t('testing:blocks.titles.result_subject')}
                onClose={onClose}
                renderFooter={
                    <>
                        <Button variant='outlined' onClick={onClose}>
                            {t('common:actions.close')}
                        </Button>
                    </>
                }
            >
                {content}
            </Drawer>

            <StudentSubjectDetailedResultsDrawer
                currentQuestionNumber={currentQuestionNumber}
                questions={testResults.questions}
                subjectName={subject?.name}
                onChange={handleChangeCurrentQuestionNumber}
                onClose={handleCloseDetailedResults}
            />
        </>
    );
};

export { StudentSubjectResultsDrawer };
